import React from 'react';

import { ExternalLink, TradingBlockLink } from '@tradingblock/components';
import { ConfigType, config as clientConfig } from '../clientConfig';

export interface LoginCardAuthFooterProps {
  isVirtual: boolean;
  group: 'mb' | 'tb';
  siteParam: string;
  environmentConfig: ConfigType;
  /** Primary action under the main form (Forgot password, Contact us, Log in, etc.) */
  recovery?: React.ReactNode;
  /** When false, only the recovery row is shown (e.g. 2FA step on login). */
  showSecondaryNav?: boolean;
  /**
   * When true, skip the recovery strip and render only the divider + secondary links
   * (e.g. email verification — body content is the main message).
   */
  omitRecoveryRow?: boolean;
  /**
   * Optional href for the virtual "Open account" link when the default
   * `virtual.applicationUrl` is not appropriate (e.g. email verification uses `applicationUrl`).
   */
  virtualOpenAccountHrefOverride?: string;
}

/**
 * Shared login-card footer: optional recovery link, divider, stacked Open account / Virtual trading links.
 * Used across apiserver auth screens for consistent hierarchy and styling.
 */
export const LoginCardAuthFooter: React.FC<LoginCardAuthFooterProps> = ({
  isVirtual,
  group,
  siteParam,
  environmentConfig,
  recovery,
  showSecondaryNav = true,
  omitRecoveryRow = false,
  virtualOpenAccountHrefOverride,
}) => {
  const virtualOpenHref =
    virtualOpenAccountHrefOverride != null
      ? virtualOpenAccountHrefOverride
      : `${clientConfig.virtual.applicationUrl}${siteParam}`;

  return (
    <>
      {!omitRecoveryRow && recovery !== undefined && recovery !== null && (
        <div className="login-primary-recovery">{recovery}</div>
      )}
      {showSecondaryNav && (
        <>
          <hr className="login-primary-divider" />
          <nav className="login-primary-secondary" aria-label="Account options">
            <ul className="login-primary-secondary-list">
              <li>
                {isVirtual ? (
                  <ExternalLink className="login-primary-secondary-link" href={virtualOpenHref}>
                    Open account
                  </ExternalLink>
                ) : (
                  <TradingBlockLink
                    className="login-primary-secondary-link"
                    siteGroup={group}
                    origin={group}
                    to="OpenNewAccountUrl"
                    config={environmentConfig}
                  >
                    Open account
                  </TradingBlockLink>
                )}
              </li>
              {!isVirtual && (
                <li>
                  <ExternalLink
                    className="login-primary-secondary-link"
                    href={`${clientConfig.virtual.defaultRedirect}${siteParam}`}
                  >
                    <i className="fal fa-vr-cardboard login-primary-secondary-icon" aria-hidden="true" />
                    Virtual trading
                  </ExternalLink>
                </li>
              )}
            </ul>
          </nav>
        </>
      )}
    </>
  );
};
